/**
 * <tecton-showcase> — active-product coordination for the homepage showcase.
 *
 * Port of the state in HorizontalCardShowcase.tsx. One index drives three
 * things at once: which card is tinted, which background video is visible, and
 * which benefit line shows in the header.
 *
 * Hovering a card takes over; when the pointer leaves, the index resumes
 * auto-advancing every `data-rotate-ms` so the background never goes static.
 * Auto-advance is suppressed entirely under prefers-reduced-motion, matching
 * the original.
 */

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class TectonShowcase extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('.tecton-card')];
    this.videos = [...this.querySelectorAll('.tecton-showcase__bg-video')];
    this.benefits = [...this.querySelectorAll('.tecton-showcase__benefit[data-index]')];

    if (this.cards.length === 0) return;

    this.rotateMs = Number(this.dataset.rotateMs) || 5000;
    this.index = 0;
    this.hovered = false;
    this.reduceMotion = window.matchMedia(MOTION_QUERY).matches;

    for (const card of this.cards) {
      const i = Number(card.dataset.index);
      card.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.stopRotation();
        this.setActive(i);
      });
      card.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.startRotation();
      });
      // Keyboard users get the same coordination when focus enters a card.
      card.addEventListener('focusin', () => {
        this.stopRotation();
        this.setActive(i);
      });
    }

    this.setActive(0);
    this.startRotation();

    // Pause the whole thing off-screen — four looping videos is real battery.
    this.visibility = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!this.hovered) this.startRotation();
      } else {
        this.stopRotation();
      }
    });
    this.visibility.observe(this);
  }

  disconnectedCallback() {
    this.stopRotation();
    this.visibility?.disconnect();
  }

  setActive(index) {
    this.index = index;

    for (const card of this.cards) {
      card.toggleAttribute('data-active', Number(card.dataset.index) === index);
    }
    for (const video of this.videos) {
      video.toggleAttribute('data-active', Number(video.dataset.index) === index);
    }
    for (const benefit of this.benefits) {
      const match = Number(benefit.dataset.index) === index;
      // Toggled, not hidden — every line shares one grid cell so the slot keeps
      // the height of the tallest and the section never shifts on swap.
      benefit.toggleAttribute('data-active', match);
      if (match) {
        // Re-trigger the swap animation defined in tecton-base.css.
        benefit.classList.remove('benefit-swap');
        void benefit.offsetWidth;
        benefit.classList.add('benefit-swap');
      }
    }
  }

  startRotation() {
    if (this.reduceMotion || this.timer || this.cards.length < 2) return;
    this.timer = setInterval(() => {
      this.setActive((this.index + 1) % this.cards.length);
    }, this.rotateMs);
  }

  stopRotation() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('tecton-showcase')) {
  customElements.define('tecton-showcase', TectonShowcase);
}
